import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const WebDevelopment = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1920" 
          alt="Web Development" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Professional <span className="text-green-500">Web Development</span> Services
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Create stunning, responsive, and high-performing websites that drive results. From corporate sites to e-commerce platforms, we build digital experiences that engage and convert.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Build Your Digital Presence with <span className="text-green-600">Modern Web Solutions</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              At Plexgate, we create dynamic, user-friendly, and responsive websites tailored to your business needs. Our web development services combine cutting-edge technology with creative design to deliver websites that not only look great but perform exceptionally.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you need a corporate website, e-commerce platform, web application, or custom CMS, our experienced developers use the latest frameworks and best practices to build scalable, secure, and SEO-optimized websites that help your business grow online.
            </p>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=600&fit=crop" 
              alt="Web Development" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => {
    const services = [
      {
        title: 'Corporate Websites',
        description: 'Professional business websites with modern designs, content management systems, and responsive layouts that establish your brand online.',
        icon: '🏢'
      },
      {
        title: 'E-Commerce Solutions',
        description: 'Full-featured online stores with shopping carts, payment gateways, inventory management, and secure checkout processes.',
        icon: '🛒'
      },
      {
        title: 'Custom Web Applications',
        description: 'Tailored web applications built with React, Angular, Vue.js, and modern frameworks to meet your specific business requirements.',
        icon: '⚙️'
      },
      {
        title: 'CMS Development',
        description: 'Content management systems using WordPress, Drupal, or custom solutions that make it easy to manage your website content.',
        icon: '📝'
      },
      {
        title: 'Progressive Web Apps (PWA)',
        description: 'App-like experiences on the web with offline functionality, push notifications, and fast loading times.',
        icon: '📱'
      },
      {
        title: 'Website Maintenance',
        description: 'Ongoing support, updates, security patches, performance optimization, and content updates to keep your site running smoothly.',
        icon: '🔧'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Web Development</span> Services
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive web solutions for every business need
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Technologies Section
  const TechnologiesSection = () => {
    const technologies = {
      'Frontend': ['React', 'Vue.js', 'Next.js', 'Tailwind CSS', 'Bootstrap'],
      'Backend': ['Node.js', 'Python Django', 'PHP Laravel', 'Ruby on Rails', 'Express.js'],
      'CMS': ['WordPress', 'Drupal', 'Contentful', 'Strapi', 'Sanity'],
      'E-Commerce': ['Shopify', 'WooCommerce', 'Magento', 'PrestaShop', 'Custom'],
      'Database': ['MySQL', 'PostgreSQL', 'MongoDB', 'Firebase', 'Redis'],
      'Tools': ['Git', 'Webpack', 'Docker', 'CI/CD', 'REST APIs']
    };

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Technologies We <span className="text-green-600">Master</span>
            </h2>
            <p className="text-gray-600">We use the latest and most reliable web technologies</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(technologies).map(([category, techs], index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold mb-4 text-gray-900">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {techs.map((tech, i) => (
                    <span key={i} className="bg-white px-3 py-1 rounded-full text-sm text-gray-700 border border-gray-200">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Development Process Section
  const ProcessSection = () => {
    const steps = [
      {
        number: '01',
        title: 'Discovery & Planning',
        description: 'We analyze your requirements, research your target audience, plan site structure, and create detailed specifications.'
      },
      {
        number: '02',
        title: 'Design & Prototyping',
        description: 'Our designers create wireframes, mockups, and interactive prototypes that reflect your brand and user needs.'
      },
      {
        number: '03',
        title: 'Development & Testing',
        description: 'We build your website using modern technologies, conduct thorough testing, and ensure cross-browser compatibility.'
      },
      {
        number: '04',
        title: 'Launch & Support',
        description: 'We deploy your website, provide training, and offer ongoing maintenance and support to ensure success.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Development <span className="text-green-600">Process</span>
            </h2>
            <p className="text-gray-600">From concept to launch, we follow a proven methodology</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Features Section
  const FeaturesSection = () => {
    const features = [
      {
        title: 'Responsive Design',
        description: 'Websites that look perfect on all devices - desktop, tablet, and mobile.',
        icon: '📱'
      },
      {
        title: 'SEO Optimized',
        description: 'Built-in SEO best practices to help your website rank higher in search results.',
        icon: '🔍'
      },
      {
        title: 'Fast Loading',
        description: 'Optimized performance with lightning-fast page load times for better user experience.',
        icon: '⚡'
      },
      {
        title: 'Secure & Reliable',
        description: 'SSL certificates, security patches, and regular backups to protect your website.',
        icon: '🔒'
      },
      {
        title: 'Scalable Architecture',
        description: 'Built to grow with your business, easily expandable with new features.',
        icon: '📈'
      },
      {
        title: 'Easy to Manage',
        description: 'User-friendly content management systems that anyone can update.',
        icon: '👥'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="text-green-600">Our Web Solutions</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{feature.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Build Your <span className="text-green-500">Dream Website</span>?
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Let's create a stunning website that represents your brand and drives business growth.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Start Your Project →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="web-development-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <TechnologiesSection />
      <ProcessSection />
      <FeaturesSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};



  // FAQ Section
  const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
    const faqs = [
      {
        question: 'How long does it take to build a website?',
        answer: 'A basic website takes 4-6 weeks, while complex e-commerce or custom web applications can take 3-6 months. Timeline depends on features, design complexity, and content readiness.'
      },
      {
        question: 'Do you provide website hosting?',
        answer: 'Yes! We offer reliable hosting solutions or can work with your preferred hosting provider. We also provide domain registration, SSL certificates, and email hosting.'
      },
      {
        question: 'Can I update the website content myself?',
        answer: 'Absolutely! We build websites with user-friendly content management systems (CMS) like WordPress or custom admin panels. We also provide training on how to manage your content.'
      },
      {
        question: 'Will my website be mobile-friendly?',
        answer: 'Yes, all websites we build are fully responsive and optimized for mobile devices, tablets, and desktops. Mobile-first design is our standard approach.'
      },
      {
        question: 'Do you redesign existing websites?',
        answer: 'Yes! We offer website redesign services to modernize outdated sites, improve user experience, enhance performance, and implement new features while preserving your existing content and SEO.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-green-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
  type="button"
  className="w-full px-6 py-4 flex justify-between..."
>
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <span className="text-2xl text-gray-600">
                    {expandedFAQ === index ? '−' : '+'}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };


export default WebDevelopment;